import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  GlobalMessageService,
  GlobalMessageType,
  TranslationService,
} from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import { UigB2BUser } from 'src/app/organization/uig-user/uig-user.model';
import { CustomVendorReportingUtils } from '../../custom-vendor-reporting-utils';
import {
  CustomVendorReportingOverviewList,
  CustomVendorReportingSearchFilters,
  CustomVendorReportingSortOptions,
  ReportCategory,
  ReportChannel,
  ReportTransferStatus,
  ReportTransferType,
  VendorReportTransferData,
} from '../../custom-vendor-reporting.models';
import { CustomVendorReportingService } from '../../custom-vendor-reporting.service';
import {
  clearVendorReportingOverview,
  clearVendorReportingSearchFilters,
  getVendorReportingOverview,
} from '../../store/custom-vendor-reporting.action';
import { CustomVendorReportingState } from '../../store/custom-vendor-reporting.reducer';
import {
  errorVendorReportingOverview,
  selectVendorReportingFilters,
  selectVendorReportingOverview,
} from '../../store/custom-vendor-reporting.selector';


@Component({
    selector: 'app-custom-vendor-reporting',
    templateUrl: './custom-vendor-reporting.component.html',
    styleUrls: ['./custom-vendor-reporting.component.scss'],
    standalone: false
})
export class CustomVendorReportingComponent implements OnInit, OnDestroy {
  @ViewChild('searchForm') searchForm: NgForm;
  reportTransferStatusEnum = ReportTransferStatus;

  subscription = new Subscription();

  vendorReportingOverview$: Observable<CustomVendorReportingOverviewList> =
    this.store.select(selectVendorReportingOverview);

  errorOverview$: Observable<Error> = this.store.select(
    errorVendorReportingOverview
  );

  searchFilters$: Observable<CustomVendorReportingSearchFilters> = this.store
    .select(selectVendorReportingFilters)
    .pipe(
      tap((filters) => {
        this.filters = { ...filters };
        this.setSearchFiltersDates(this.filters);
      })
    );

  manufacturersList$ = new  Observable<String[]>()

  vendorReportingOverview: CustomVendorReportingOverviewList;
  errorOverview: Error;

  vendorReportTransferDataList: VendorReportTransferData[];

  selectedSortOption: string;
  sortOptions: string[];
  sortDirection: string = 'desc';

  showSuccessfullyTransferred: boolean;
  filterSuccessfullyTransferred$ = new BehaviorSubject<boolean>(false);
  isFilteringrSuccessfullyTransferred$ = new BehaviorSubject<boolean>(false);

  filters: CustomVendorReportingSearchFilters;

  fileName: string = '';
  fileToBeImported: File;

  currentUserB2BUnit: string;
  defaultReportCategory: ReportCategory;
  reportTransferTypesList = Object.values(ReportTransferType).filter(
    (value) => typeof value === 'string'
  ) as string[];

  reportTransferStatusesList = Object.values(ReportTransferStatus).filter(
    (value) => typeof value === 'string'
  ) as string[];

  reportCategoryTypesList = Object.values(ReportCategory).filter(
    (value) => typeof value === 'string'
  ) as string[];

  reportChannelList = Object.values(ReportChannel).filter(
    (value) => typeof value === 'string'
  ) as string[];

  isRioEmployee: boolean=false;

  constructor(
    private store: Store<CustomVendorReportingState>,
    private translation: TranslationService,
    private messageService: GlobalMessageService,
    private datePipe: DatePipe,
    private vendorReportingService: CustomVendorReportingService,
    private userService: UserAccountFacade,
  ) {}

  ngOnInit(): void {
    this.setSortOptions();

    this.subscription.add(
      combineLatest([this.searchFilters$.pipe(first(f => !!f)),
      this.userService
        .get()
        .pipe(
          filter((user: UigB2BUser) => user?.orgUnit !== undefined),
          map((user) => user?.orgUnit?.uid)
        )])
        .subscribe(([,unitId]) => {
          this.currentUserB2BUnit = unitId;
          if (unitId === CustomVendorReportingUtils.rioEmployeeB2BUnit) {
            this.isRioEmployee = true;
            this.sortOptions.push("b2bUnit");
            this.filters.reportCategory = ReportCategory[ReportCategory.Daily];
            this.setReportTransferPeriodOnReportCategoryChange(this.filters);
          }
          this.getVendorReportingOverview(this.filters);
        })
    );

    combineLatest([
      this.vendorReportingOverview$,
      this.filterSuccessfullyTransferred$,
    ])
      .pipe(
        filter(([vendorReportingOverview]) => vendorReportingOverview != null),
        tap(([vendorReportingOverview]) => {
          this.vendorReportingOverview = vendorReportingOverview;
          this.vendorReportTransferDataList = [
            ...vendorReportingOverview.responses,
          ];
          this.filterSuccessfullyTransferred();
          this.loadManufacturersDropdown();
          this.onChangeSort();
          this.isFilteringrSuccessfullyTransferred$.next(false);
        })
      )
      .subscribe();

    this.errorOverview$
      .pipe(
        filter((error) => error != null),
        tap((error) => {
          this.errorOverview = error;
        }),
        switchMap(() =>
          this.translation.translate('customVendorReporting.error')
        ),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
        })
      )
      .subscribe();
  }

  loadManufacturersDropdown() {
    this.manufacturersList$ = this.vendorReportingService.getManufacturerMappings()
    .pipe(map(manufactuersList => {
      const manufacturers = manufactuersList.map(m => m.manufacturer);
      return manufacturers.filter(m => this.vendorReportTransferDataList.map(transferData => transferData.manufacturer).includes(m));
    }));
  }

  setSearchFiltersDates(filters: CustomVendorReportingSearchFilters) {
    filters.transferDateFrom =
      filters.transferDateFrom === ''
        ? this.datePipe.transform(
            CustomVendorReportingUtils.getDefaultTransferDateFrom(new Date()),
            CustomVendorReportingUtils.DATE_FIELDS_FORMAT
          )
        : filters.transferDateFrom;
  }

  setSearchFiltersDatesForRioDashboards(filters: CustomVendorReportingSearchFilters) {
    filters.transferDateFrom =
      filters.transferDateFrom === ''
        ? this.datePipe.transform(
      CustomVendorReportingUtils.applyRioDashBoardDateFilter(filters.reportCategory,new Date()),
      CustomVendorReportingUtils.DATE_FIELDS_FORMAT
    ):filters.transferDateFrom;
  }

  setReportTransferPeriodOnReportCategoryChange(filters: CustomVendorReportingSearchFilters) {
    filters.transferDateFrom =
       this.datePipe.transform(
      CustomVendorReportingUtils.applyRioDashBoardDateFilter(filters.reportCategory,new Date()),
      CustomVendorReportingUtils.DATE_FIELDS_FORMAT
    );
  }
  filterSuccessfullyTransferred(): void {
    if (this.showSuccessfullyTransferred) {
      this.vendorReportTransferDataList = [
        ...this.vendorReportingOverview.responses,
      ];
      return;
    }
    this.vendorReportTransferDataList = [
      ...this.vendorReportTransferDataList.filter(
        (data) =>
          data.reportTransferStatus !==
          ReportTransferStatus[ReportTransferStatus.Success]
      ),
    ];
  }

  fireFilterSuccessfullyTransferred(): void {
    this.isFilteringrSuccessfullyTransferred$.next(true);
    this.filterSuccessfullyTransferred$.next(this.showSuccessfullyTransferred);
  }

  setSortOptions(): void {
    this.selectedSortOption =
      CustomVendorReportingSortOptions[
        CustomVendorReportingSortOptions.reportTransferPeriod
      ];
    this.sortOptions = Object.values(CustomVendorReportingSortOptions).filter(
      (value) => typeof value === 'string'
    ) as string[];
  }

  getVendorReportingOverview(
    filters: CustomVendorReportingSearchFilters = CustomVendorReportingUtils.initSearchFilters()
  ): void {
    this.vendorReportingOverview = null;
    this.vendorReportTransferDataList = null;
    this.errorOverview = null;
    if (!this.isRioEmployee) {
      filters.b2bUnit = this.currentUserB2BUnit;
      filters.reportCategory='';
      this.setSearchFiltersDates(filters);
    }else{
      this.setSearchFiltersDatesForRioDashboards(filters);
    }
    this.store.dispatch(getVendorReportingOverview({ searchFilters: filters }));
  }

  onSubmit(): void {
    this.getVendorReportingOverview(this.filters);
  }

  resetForm(): void {
    this.sortDirection = 'desc';
    this.setSortOptions();
    this.getVendorReportingOverview();
  }

  onChangeSort(): void {
    this.vendorReportTransferDataList = this.vendorReportTransferDataList.sort(
      (a, b) => {
        const res = CustomVendorReportingUtils.compare(
          this.selectedSortOption,
          a[this.selectedSortOption],
          b[this.selectedSortOption]
        );
        return this.sortDirection === 'asc' ? res : -res;
      }
    );
  }

  onReportCategoryChange() {
    // Handle the change event here
    this.setReportTransferPeriodOnReportCategoryChange(this.filters);
     this.selectedSortOption =
    CustomVendorReportingSortOptions[
      CustomVendorReportingSortOptions.reportTransferPeriod
    ];
    this.sortDirection = 'desc';

  }

  validateTransferDateFrom(): void {
    if (
      new Date(this.filters.transferDateFrom) >
      new Date(this.filters.transferDateTo)
    ) {
      this.filters.transferDateTo = this.filters.transferDateFrom;
    }
  }

  validateTransferDateTo(): void {
    if (
      new Date(this.filters.transferDateTo) <
      new Date(this.filters.transferDateFrom)
    ) {
      this.filters.transferDateFrom = this.filters.transferDateTo;
    }
  }
  getReportTransferStatusColor(status: ReportTransferStatus): string {
    return CustomVendorReportingUtils.getReportTransferStatusColor(status);
  }

  openDetails(reportTransferData: VendorReportTransferData): void {
    const modalRef =
      this.vendorReportingService.openDetails(reportTransferData,this.isRioEmployee);
    modalRef.closed.subscribe(() =>
      this.getVendorReportingOverview(this.filters)
    );
  }

  openInstructions() {
    this.vendorReportingService.openInstructions();
  }

  onFileSelected(event: any) {
    this.fileToBeImported = event.target.files[0];
    if (this.fileToBeImported) {
      this.fileName = this.fileToBeImported.name;
    }
  }

  importFile(): void {
    if (this.fileToBeImported) {
      this.subscription.add(
        this.vendorReportingService
          .importFile(this.fileToBeImported)
          .subscribe((res) => this.getVendorReportingOverview(this.filters))
      );
    }
    this.fileToBeImported = null;
    this.fileName = '';
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearVendorReportingOverview());
    this.store.dispatch(clearVendorReportingSearchFilters());
    this.subscription.unsubscribe();
  }
}
